import { useState, useCallback, KeyboardEvent } from 'react'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { useBrandProfile } from 'hooks/queries/workspaces/useBrandProfile'
import { useUpdateBrandProfile } from 'hooks/queries/brandProfiles/useUpdateBrandProfile'
import { useUpdateBrandKit } from 'hooks/queries/brandKits/useUpdateBrandKit'
import { useCardDismissal } from 'hooks/queries/cardDismissals/useCardDismissal'
import { BrandPhotosStep, QuestionnaireBrandPhoto } from './steps/BrandPhotosStep'
import { MarketingDocsStep } from './steps/MarketingDocsStep'
import { GoogleDriveStep } from './steps/GoogleDriveStep'
import { SocialAccountsStep } from './steps/SocialAccountsStep'
import { ArrowLeft } from 'blaze-ui/icons'
import Styles from './QuestionnairePanel.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { Heading } from 'blaze-ui/Heading/Heading'
import cx from 'classnames'
import { Button } from 'blaze-ui/Button'
import type { AlmanacFile } from 'types'

type StepType = 'text_input' | 'brand_photos' | 'marketing_docs' | 'google_drive' | 'social_accounts'

export enum StepId {
  BrandPersonality = 'brand_personality',
  WordsToAvoid = 'words_to_avoid',
  DemographicsPainPoints = 'demographics_pain_points',
  Competitors = 'competitors',
  BrandPhotos = 'brand_photos',
  MarketingDocs = 'marketing_docs',
  GoogleDrive = 'google_drive',
  SocialAccounts = 'social_accounts',
}

interface QuestionnaireStep {
  id: StepId
  type: StepType
  question: string
  hint?: string
  placeholder?: string
  saveTarget?: 'brand_profile' | 'content_preferences'
  sectionTitle?: string
  valueFormat?: 'sentence_sounds_like' | 'bullet_list' | 'pain_points'
}

export const STEPS: QuestionnaireStep[] = [
  {
    id: StepId.BrandPersonality,
    type: 'text_input',
    question: 'What brands sound like you?',
    hint: 'e.g. Notion, Mailchimp, Apple',
    placeholder: 'Type here',
    saveTarget: 'brand_profile',
    sectionTitle: 'Brand Personality',
    valueFormat: 'sentence_sounds_like',
  },
  {
    id: StepId.WordsToAvoid,
    type: 'text_input',
    question: "Words you don't wanna use?",
    hint: 'e.g. synergy, leverage, disrupt',
    placeholder: 'Type here',
    saveTarget: 'content_preferences',
  },
  {
    id: StepId.DemographicsPainPoints,
    type: 'text_input',
    question: 'What are the pain points of your target customer?',
    hint: 'e.g. time management, budget constraints',
    placeholder: 'Type here',
    saveTarget: 'brand_profile',
    sectionTitle: 'Primary Customer Segments',
    valueFormat: 'pain_points',
  },
  {
    id: StepId.Competitors,
    type: 'text_input',
    question: 'Who are your competitors?',
    hint: 'e.g. Nike, Adidas',
    placeholder: 'Type here',
    saveTarget: 'brand_profile',
    sectionTitle: 'Direct Competitors',
    valueFormat: 'bullet_list',
  },
  {
    id: StepId.BrandPhotos,
    type: 'brand_photos',
    question: 'Add brand photos',
    hint: 'Add source materials for better content every generation',
  },
  {
    id: StepId.MarketingDocs,
    type: 'marketing_docs',
    question: 'Add your key marketing docs',
    hint: 'Product docs, marketing descriptions, sample brochures',
  },
  {
    id: StepId.GoogleDrive,
    type: 'google_drive',
    question: 'Connect your drive',
    hint: 'Add source materials for better content every generation',
  },
  {
    id: StepId.SocialAccounts,
    type: 'social_accounts',
    question: 'Connect your social accounts',
    hint: 'Enables auto posting and learns what performs best',
  },
]

interface QuestionnairePanelProps {
  subtitle?: string
  brandMediaSubtitle?: string
  hintAsPlaceholder?: boolean
  variant?: string
  overrideStep?: number
  onStepChange?: React.Dispatch<React.SetStateAction<number>>
  hiddenSteps?: StepId[]
  onFinish?: () => void
}

export const QuestionnairePanel = observer(
  ({
    subtitle = 'While you wait...',
    brandMediaSubtitle,
    hintAsPlaceholder = false,
    variant = '',
    overrideStep,
    onStepChange,
    hiddenSteps = [],
    onFinish,
  }: QuestionnairePanelProps) => {
    const { contentGeneration: store } = useRootStore()
    const [currentIndex, setCurrentIndex] = useState(0)

    const indexToUse = overrideStep !== undefined ? overrideStep : currentIndex
    const setCurrentIndexToUse = (overrideStep !== undefined ? onStepChange : setCurrentIndex) || setCurrentIndex

    const { dismissCard } = useCardDismissal('generation_questionnaire')

    const [textStepValues, setTextStepValues] = useState<Partial<Record<StepId, string>>>({})
    const [brandPhotoItems, setBrandPhotoItems] = useState<QuestionnaireBrandPhoto[]>([])

    const appendPhotosFromUpload = useCallback((files: AlmanacFile[]) => {
      setBrandPhotoItems((prev) => [
        ...prev,
        ...files.map((f) => ({
          id: f.id,
          thumbnail_url: f.thumbnail_url,
          source_url: f.source_url,
          duration: f.duration,
        })),
      ])
    }, [])

    const steps = STEPS.filter((step) => !hiddenSteps.includes(step.id))
    const currentStep = steps[indexToUse]
    const isLastStep = indexToUse >= steps.length - 1

    const finishQuestionnaire = useCallback(() => {
      store.setShowQuestionnaire(false)
      dismissCard()
      onFinish?.()
    }, [store, dismissCard, onFinish])

    const advance = useCallback(() => {
      if (isLastStep) {
        finishQuestionnaire()
      } else {
        setCurrentIndexToUse((i) => i + 1)
      }
    }, [isLastStep, finishQuestionnaire, setCurrentIndexToUse])

    const goBack = useCallback(() => {
      setCurrentIndexToUse((i) => Math.max(0, i - 1))
    }, [setCurrentIndexToUse])

    if (!currentStep) return null

    const isFirstStep = indexToUse === 0

    return (
      <div className={cx(Styles.panel, Styles[variant], { [Styles.brandPhotos]: currentStep.type === 'brand_photos' })}>
        {!isFirstStep && variant !== 'onboarding' && (
          <button className={Styles.backButton} onClick={goBack} aria-label="Back">
            <ArrowLeft size={16} />
          </button>
        )}
        <div className={Styles.content}>
          {(currentStep.type !== 'brand_photos' || variant !== 'onboarding') && (
            <Text variant="secondary">{subtitle}</Text>
          )}
          <Heading level={2}>{currentStep.question}</Heading>
          {currentStep.type === 'brand_photos' && brandMediaSubtitle && (
            <Text variant="secondary">{brandMediaSubtitle}</Text>
          )}
          {!!currentStep.hint && !hintAsPlaceholder && (
            <div className={Styles.hint}>
              <Text variant="secondary" color="var(--dark-40)">
                {currentStep.hint}
              </Text>
            </div>
          )}
          <StepContent
            step={{
              ...currentStep,
              placeholder: hintAsPlaceholder ? currentStep.hint : currentStep.placeholder,
            }}
            textInputValue={textStepValues[currentStep.id] ?? ''}
            onTextInputChange={(value) => setTextStepValues((prev) => ({ ...prev, [currentStep.id]: value }))}
            brandPhotoItems={brandPhotoItems}
            onBrandPhotosUploaded={appendPhotosFromUpload}
            onAdvance={advance}
            onSkip={advance}
            verticalFooter={variant === 'onboarding'}
            isOnboarding={variant === 'onboarding'}
            autoFocus={variant !== 'onboarding'}
          />
        </div>
      </div>
    )
  },
)

interface StepContentProps {
  step: QuestionnaireStep
  textInputValue: string
  onTextInputChange: (value: string) => void
  brandPhotoItems: QuestionnaireBrandPhoto[]
  onBrandPhotosUploaded: (files: AlmanacFile[]) => void
  onAdvance: () => void
  onSkip: () => void
  verticalFooter?: boolean
  isOnboarding?: boolean
  autoFocus?: boolean
}

function StepContent(props: StepContentProps) {
  switch (props.step.type) {
    case 'text_input':
      return <TextInputStep value={props.textInputValue} onValueChange={props.onTextInputChange} {...props} />
    case 'brand_photos':
      return (
        <BrandPhotosStep
          localPhotos={props.brandPhotoItems}
          onPhotosUploaded={props.onBrandPhotosUploaded}
          onAdvance={props.onAdvance}
          onSkip={props.onSkip}
          isOnboarding={props.isOnboarding}
        />
      )
    case 'marketing_docs':
      return <MarketingDocsStep onAdvance={props.onAdvance} onSkip={props.onSkip} />
    case 'google_drive':
      return <GoogleDriveStep onAdvance={props.onAdvance} onSkip={props.onSkip} />
    case 'social_accounts':
      return <SocialAccountsStep />
    default:
      return null
  }
}

function TextInputStep({
  step,
  value,
  onValueChange,
  onAdvance,
  onSkip,
  verticalFooter,
  autoFocus = true,
}: {
  step: QuestionnaireStep
  value: string
  onValueChange: (value: string) => void
  onAdvance: () => void
  onSkip: () => void
  verticalFooter?: boolean
  autoFocus?: boolean
}) {
  const [isSaving, setIsSaving] = useState(false)

  const { data: brandProfile } = useBrandProfile()
  const { mutateAsync: updateBrandProfile } = useUpdateBrandProfile({ id: brandProfile?.id })
  const brandKitId = brandProfile?.brand_kit?.id
  const { mutateAsync: updateBrandKit } = useUpdateBrandKit({ id: brandKitId || 0 })

  const hasInput = value.trim().length > 0

  const saveBrandProfileSection = useCallback(
    async (sectionTitle: string, value: string) => {
      if (!brandProfile) return
      const profileText =
        typeof brandProfile.profile === 'string' ? brandProfile.profile : JSON.stringify(brandProfile.profile)
      const updated = appendToSection(profileText, sectionTitle, value)
      await updateBrandProfile({ profile: updated })
    },
    [brandProfile, updateBrandProfile],
  )

  const saveContentPreferences = useCallback(
    async (value: string) => {
      if (!brandKitId) return
      const newWords = value
        .split(',')
        .map((w) => w.trim())
        .filter(Boolean)
      const existing = brandProfile?.brand_kit?.content_preferences?.words_and_phrases_to_avoid || []
      const merged = [...new Set([...existing, ...newWords])]
      await updateBrandKit({ content_preferences: { words_and_phrases_to_avoid: merged } })
    },
    [brandKitId, brandProfile, updateBrandKit],
  )

  const handleSubmit = useCallback(async () => {
    if (!hasInput || isSaving) return
    setIsSaving(true)
    try {
      const raw = value.trim()
      const formatted = formatStepValue(raw, step.valueFormat)
      if (step.saveTarget === 'brand_profile' && step.sectionTitle) {
        await saveBrandProfileSection(step.sectionTitle, formatted)
      } else if (step.saveTarget === 'content_preferences') {
        await saveContentPreferences(raw)
      }
    } finally {
      setIsSaving(false)
    }
    onAdvance()
  }, [hasInput, isSaving, value, step, saveBrandProfileSection, saveContentPreferences, onAdvance])

  const handleSkip = useCallback(() => {
    onValueChange('')
    onSkip()
  }, [onSkip, onValueChange])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (hasInput) handleSubmit()
      }
    },
    [hasInput, handleSubmit],
  )

  return (
    <>
      <div className={Styles.inputWrapper}>
        <input
          className={Styles.input}
          type="text"
          placeholder={step.placeholder}
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus={autoFocus}
        />
      </div>
      {verticalFooter ? (
        <div className={Styles.verticalActions}>
          <Button onPress={handleSubmit} isDisabled={isSaving} fullWidth size="lg">
            {isSaving ? 'Saving...' : 'Continue'}
          </Button>
          <Button variant="tertiary" fullWidth size="sm" color="var(--dark-60)" onPress={handleSkip}>
            Skip Question
          </Button>
        </div>
      ) : (
        <div className={Styles.actions}>
          {hasInput ? (
            <>
              <Button className={Styles.submitButton} onPress={handleSubmit} size="lg" isDisabled={isSaving}>
                {isSaving ? 'Saving...' : 'Looks good!'}
              </Button>
              <span className={Styles.enterHint}>or press enter ↵</span>
            </>
          ) : (
            <Button className={Styles.skipButton} onPress={handleSkip} size="lg" variant="secondary">
              Skip
            </Button>
          )}
        </div>
      )}
    </>
  )
}

function formatStepValue(raw: string, format: QuestionnaireStep['valueFormat']): string {
  const items = raw
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)

  switch (format) {
    case 'sentence_sounds_like':
      return `* **Sounds like:** ${items.join(', ')}`
    case 'bullet_list':
      return items.map((item) => `* ${item}`).join('\n')
    case 'pain_points':
      return `* **Pain points:** ${items.join(', ')}`
    default:
      return raw
  }
}

function appendToSection(markdown: string, sectionTitle: string, value: string): string {
  const lines = markdown.split('\n')
  const normalizedTarget = sectionTitle.toLowerCase()

  const headerIndex = lines.findIndex((line) => {
    const match = line.match(/^(#{2,4})\s+(.*)/)
    if (!match) return false
    const title = match[2].replace(/:$/, '').trim().toLowerCase()
    return title === normalizedTarget
  })

  if (headerIndex === -1) {
    return markdown.trimEnd() + `\n#### ${sectionTitle}:\n${value}\n`
  }

  const matchedLevel = lines[headerIndex].match(/^(#{2,4})\s/)?.[1] || '##'
  const matchedDepth = matchedLevel.length

  let insertAt = headerIndex + 1
  while (insertAt < lines.length) {
    const nextMatch = lines[insertAt].match(/^(#{2,4})\s/)
    if (nextMatch && nextMatch[1].length <= matchedDepth) break
    insertAt++
  }

  while (insertAt > headerIndex + 1 && lines[insertAt - 1].trim() === '') {
    insertAt--
  }

  lines.splice(insertAt, 0, value)
  return lines.join('\n')
}
